import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Quote, History, Repeat, FileDown } from "lucide-react";

function ScoreRing({ score }) {
  const radius = 64;
  const stroke = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, score)) / 100) * circumference;
  return (
    <div className="relative w-44 h-44 grid place-items-center">
      <svg width="176" height="176" className="-rotate-90">
        <circle cx="88" cy="88" r={radius} stroke="#e7e5e4" strokeWidth={stroke} fill="none" />
        <circle
          cx="88" cy="88" r={radius}
          stroke="#b81f24"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-display text-5xl font-light text-stone-900" data-testid="overall-score">{score}</div>
          <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mt-1">out of 100</div>
        </div>
      </div>
    </div>
  );
}

function DimensionBar({ label, value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-stone-700">{label}</span>
        <span className="font-medium text-stone-900" data-testid={`dim-score-${label}`}>{value}</span>
      </div>
      <div className="mt-1.5 h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#b81f24] rounded-full" style={{ width: `${pct}%`, transition: "width 0.8s ease-out" }} />
      </div>
    </div>
  );
}

const COMPETENCY_ORDER = [
  "Managing Others",
  "Communication",
  "Motivating Others",
  "Delivering Results",
  "Coaching And Developing",
  "Action Oriented",
];

function deriveCompetencies(ev) {
  // Use stored competency scores if available
  const stored = ev.competencies || {};
  if (COMPETENCY_ORDER.some((c) => stored[c] != null && stored[c] > 0)) return stored;
  // Derive from dimensions (same formula as backend analytics._derive_competency)
  const { empathy = 0, clarity = 0, assertiveness = 0, listening = 0 } = ev.dimensions || {};
  return {
    "Managing Others": Math.round((assertiveness + clarity) / 2),
    "Communication": clarity,
    "Motivating Others": Math.round((empathy + assertiveness) / 2),
    "Delivering Results": Math.round((clarity + assertiveness) / 2),
    "Coaching And Developing": Math.round((listening + empathy) / 2),
    "Action Oriented": assertiveness,
  };
}

export default function PostSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/sessions/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leadersim-report-${id.slice(-6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    api.get(`/sessions/${id}`)
      .then((res) => {
        if (res.data.status !== "completed") {
          // try to end it
          return api.post(`/sessions/${id}/end`).then((r) => setSession(r.data));
        }
        setSession(res.data);
      })
      .catch((err) => {
        toast.error(formatApiError(err));
        navigate("/history");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <div className="font-display text-2xl text-stone-900">Scoring your conversation…</div>
          <div className="mt-3 text-stone-500 text-sm">This usually takes a few seconds.</div>
        </div>
      </div>
    );
  }

  const ev = session.evaluation || {};
  const dims = ev.dimensions || {};
  const competencies = deriveCompetencies(ev);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-3">Session review</div>
        <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">{session.scenario.title}</h1>
        <p className="mt-2 text-stone-600 max-w-xl">With {session.scenario.persona_name} · {session.scenario.persona_role}</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Score + dimensions */}
          <section className="lg:col-span-5 bg-white border border-stone-200 rounded-3xl p-8 flex flex-col items-center text-center">
            <ScoreRing score={ev.overall_score ?? 0} />
            <p className="mt-6 text-stone-700 max-w-sm leading-relaxed" data-testid="evaluation-summary">{ev.summary}</p>

            <div className="mt-8 w-full space-y-4">
              <DimensionBar label="Empathy" value={dims.empathy ?? 0} />
              <DimensionBar label="Clarity" value={dims.clarity ?? 0} />
              <DimensionBar label="Assertiveness" value={dims.assertiveness ?? 0} />
              <DimensionBar label="Listening" value={dims.listening ?? 0} />
            </div>
          </section>

          {/* Strengths + Improvements */}
          <section className="lg:col-span-7 grid grid-cols-1 gap-6">
            <div className="bg-white border border-stone-200 rounded-3xl p-7" data-testid="strengths-card">
              <div className="text-[10px] tracking-[0.18em] uppercase text-emerald-700 mb-3">Strengths</div>
              <ul className="space-y-3">
                {(ev.strengths || []).map((s, i) => (
                  <li key={i} className="flex gap-3 text-stone-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-stone-200 rounded-3xl p-7" data-testid="improvements-card">
              <div className="text-[10px] tracking-[0.18em] uppercase text-orange-700 mb-3">What to try next time</div>
              <ul className="space-y-3">
                {(ev.improvements || []).map((s, i) => (
                  <li key={i} className="flex gap-3 text-stone-700">
                    <ArrowUpRight className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {(ev.key_moments || []).length > 0 && (
              <div className="bg-white border border-stone-200 rounded-3xl p-7" data-testid="key-moments-card">
                <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-4">Key moments</div>
                <div className="space-y-5">
                  {ev.key_moments.map((m, i) => (
                    <div key={i} className="border-l-2 border-stone-200 pl-5">
                      <div className="flex gap-2 items-start text-stone-900">
                        <Quote className="w-4 h-4 text-stone-400 mt-1 shrink-0" strokeWidth={1.5} />
                        <span className="italic">{m.quote}</span>
                      </div>
                      <div className="text-sm text-stone-600 mt-1.5 leading-relaxed">{m.comment}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Competencies — full-width section matching the PDF */}
        <section
          className="mt-6 bg-white border border-stone-200 rounded-3xl p-7"
          data-testid="competencies-card"
        >
          <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-5">Competencies</div>
          {(() => {
            const compVals = COMPETENCY_ORDER.map(n => competencies[n]).filter(v => v != null && v >= 0);
            const compMax = compVals.length && Math.max(...compVals) <= 10 ? 10 : 100;
            const avgRaw = compVals.length ? compVals.reduce((a, b) => a + b, 0) / compVals.length : 0;
            const competencyScore = Math.round(compMax === 10 ? avgRaw * 10 : avgRaw);
            return (
              <>
                {/* Competency Score — separate from existing Empathy/Clarity/etc. scores */}
                <div className="flex items-center gap-4 mb-6 pb-5 border-b border-stone-100">
                  <div className="flex items-baseline gap-1">
                    <span
                      className="font-display text-4xl font-light text-[#b81f24]"
                      data-testid="competency-score"
                    >
                      {competencyScore}
                    </span>
                    <span className="text-sm text-stone-500">/ 100</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-stone-900">Competency Score</div>
                    <div className="text-xs text-stone-500 mt-0.5">Average across all 6 competencies</div>
                  </div>
                </div>
                {/* Individual competency bars */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
                  {COMPETENCY_ORDER.map((name) => (
                    <DimensionBar key={name} label={name} value={competencies[name] ?? 0} max={compMax} />
                  ))}
                </div>
              </>
            );
          })()}
        </section>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Button
            data-testid="practice-again-btn"
            onClick={async () => {
              try {
                const res = await api.post("/sessions", { scenario_id: session.scenario.id });
                navigate(`/session/${res.data.id}`);
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
            className="rounded-md bg-[#b81f24] hover:bg-[#9a1a1f] text-white px-6 h-11"
          >
            <Repeat className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
            Practice this one again
          </Button>
          <Button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            disabled={downloading}
            variant="outline"
            className="rounded-md border-stone-300 bg-white hover:bg-stone-100 text-stone-800 px-6 h-11"
          >
            <FileDown className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
            {downloading ? "Generating PDF…" : "Export PDF report"}
          </Button>
          <Link to="/scenarios">
            <Button variant="outline" className="rounded-md border-stone-200 bg-white hover:bg-stone-100 px-6 h-11" data-testid="more-scenarios-btn">
              Try another scenario
            </Button>
          </Link>
          <Link to="/history">
            <Button variant="ghost" className="rounded-md text-stone-600 hover:bg-stone-100 px-5 h-11" data-testid="go-history-btn">
              <History className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
              See all sessions
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
